/**
 * GoalKPIsPanel - Reusable panel for Goal KPIs management
 * 
 * Used in:
 * - GoalDetailModal (edit mode)
 * - GoalMergeModal (source view + merged form)
 * 
 * Props:
 * - kpis: array of KPI objects
 * - onAddKPI: () => void
 * - onUpdateKPI: (kpiId, field, value) => void
 * - onDeleteKPI: (kpiId) => void
 * - isEditing: boolean (controls whether KPIs can be edited)
 * - isDisabled: boolean (overrides isEditing for locked states)
 * - status: 'draft' | 'active' | 'achieved' | 'abandoned'
 * - colors: theme colors from useSafeMode
 * - computedMetrics: optional computed metrics data
 * - paceData: optional pace data for active goals
 * - goalId: required for active goals to fetch KPI charts
 * - goalStartDate: first_activated_at for pace calculations
 * - targetDate: goal target date
 * - onShowSettings: () => void - callback to show KPI settings modal
 * - onRefresh: () => void - callback to refresh goal data
 * - mode: 'edit' | 'create' | 'view'
 */

import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { ActiveKPICard } from './ActiveKPICard';
import { 
  Target, Plus, Trash2, Settings,
  TrendingUp, TrendingDown,
  ChevronDown, ChevronRight, Pencil, 
  DollarSign, Percent, Clock, Hash
} from 'lucide-react';

// Metric format options
export const METRIC_FORMAT_OPTIONS = [
  { value: 'number', label: 'Number', icon: Hash, example: '1,234' },
  { value: 'currency', label: 'Currency ($)', icon: DollarSign, example: '$1,234' },
  { value: 'percent', label: 'Percentage (%)', icon: Percent, example: '45%' },
  { value: 'time', label: 'Time', icon: Clock, example: '2h 30m' },
];

// Direction options (Outcome-Driven Model)
export const DIRECTION_OPTIONS = [
  { value: 'higher_is_better', label: 'Higher is Better', icon: TrendingUp },
  { value: 'lower_is_better', label: 'Lower is Better', icon: TrendingDown },
];

// Measurement interval options
export const INTERVAL_OPTIONS = [
  { value: 'weekly', label: 'Weekly', days: 7 },
  { value: 'monthly', label: 'Monthly', days: 30 },
  { value: 'quarterly', label: 'Quarterly', days: 90 },
];

// Format value based on metric format
export const formatMetricValue = (value, format) => {
  if (value === null || value === undefined) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  
  switch (format) {
    case 'currency':
      return `$${num.toLocaleString()}`;
    case 'percent':
      return `${num}%`;
    case 'time':
      if (num >= 60) {
        const hours = Math.floor(num / 60);
        const mins = Math.round(num % 60);
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      }
      return `${num}m`;
    default:
      return num.toLocaleString();
  }
};

// Generate unique KPI ID
export const generateKPIId = () => `kpi_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const GoalKPIsPanel = ({
  kpis = [],
  onAddKPI,
  onUpdateKPI,
  onDeleteKPI,
  isEditing = true,
  isDisabled = false,
  status = 'draft',
  colors,
  computedMetrics = null,
  paceData = null,
  goalId = null,
  goalStartDate = null,
  targetDate = null,
  onShowSettings,
  onRefresh,
  mode = 'edit',
}) => {
  const [expandedKPIs, setExpandedKPIs] = useState({});

  const toggleKPIExpanded = (kpiId) => {
    setExpandedKPIs(prev => ({
      ...prev,
      [kpiId]: !prev[kpiId]
    }));
  };

  const handleKPIChange = (kpiId, field, value) => {
    if (onUpdateKPI) {
      onUpdateKPI(kpiId, field, value);
    }
  };

  const handleDeleteKPI = (kpiId) => {
    if (onDeleteKPI) {
      onDeleteKPI(kpiId);
    }
  };

  const isLocked = status !== 'draft';
  const effectiveDisabled = isDisabled || !isEditing;

  return (
    <div className="space-y-4">
      {/* Header with title and action buttons */}
      <div className="flex items-center justify-between">
        <h3 className={cn("text-sm font-semibold", colors.textPrimary)}>
          Key Performance Indicators ({kpis.length})
        </h3>
        <div className="flex items-center gap-2">
          {/* Settings Button - Only show in active mode */}
          {status === 'active' && kpis.length > 0 && onShowSettings && (
            <Button
              size="sm"
              variant="outline"
              onClick={onShowSettings}
              className={cn("gap-1", colors.borderPrimary)}
              title="KPI Status Settings"
            >
              <Settings className="w-4 h-4" />
              Settings
            </Button>
          )}
          {/* Add KPI Button - Only show in draft mode when editing */}
          {status === 'draft' && isEditing && onAddKPI && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAddKPI}
              className={cn("gap-1", colors.borderPrimary)}
              data-testid="add-kpi-button"
            >
              <Plus className="w-4 h-4" />
              Add KPI
            </Button>
          )}
        </div>
      </div>
      
      {/* KPIs List */}
      {kpis.length === 0 ? (
        <div className={cn("text-center py-8 rounded-lg border border-dashed", colors.borderPrimary)}>
          <Target className={cn("w-8 h-8 mx-auto mb-2", colors.textMuted)} />
          <p className={cn("text-sm", colors.textMuted)}>No KPIs defined yet</p>
          {status === 'draft' && isEditing && onAddKPI && (
            <Button
              size="sm"
              variant="outline"
              onClick={onAddKPI}
              className="mt-3"
              data-testid="add-first-kpi-button"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Your First KPI
            </Button>
          )}
        </div>
      ) : status === 'active' ? (
        /* ============================================
           ACTIVE MODE: Chart-first scoreboard view
           ============================================ */
        <div className="space-y-4">
          {kpis.map((kpi) => {
            const computed = computedMetrics?.kpis_computed?.find(k => k.kpi_id === kpi.kpi_id);
            const paceDetail = paceData?.kpi_pace_details?.find(p => p.kpi_id === kpi.kpi_id);
            return (
              <ActiveKPICard
                key={kpi.kpi_id}
                goalId={goalId}
                kpi={kpi}
                computed={computed}
                paceDetail={paceDetail}
                colors={colors}
                goalStartDate={goalStartDate}
                targetDate={targetDate}
                defaultExpanded={false}
                aggregateMethodLabel={paceData?.aggregate?.method_label}
                isLoading={!paceData}
                onRefresh={onRefresh}
              />
            );
          })}
        </div>
      ) : (
        /* ============================================
           DRAFT/VIEW MODE: Editable cards
           ============================================ */
        <div className="space-y-3">
          {kpis.map((kpi, index) => {
            const isExpanded = expandedKPIs[kpi.kpi_id];
            const FormatIcon = METRIC_FORMAT_OPTIONS.find(f => f.value === kpi.metric_format)?.icon || Hash;
            
            return (
              <div 
                key={kpi.kpi_id}
                className={cn("rounded-lg border overflow-hidden", colors.borderPrimary, colors.bgTertiary)}
                data-testid={`kpi-card-${kpi.kpi_id}`}
              >
                {/* Collapsed Header */}
                <div 
                  className={cn(
                    "flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50",
                    isExpanded && "border-b " + colors.borderPrimary
                  )}
                  onClick={() => toggleKPIExpanded(kpi.kpi_id)}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {isExpanded ? (
                      <ChevronDown className={cn("w-4 h-4 flex-shrink-0", colors.textMuted)} />
                    ) : (
                      <ChevronRight className={cn("w-4 h-4 flex-shrink-0", colors.textMuted)} />
                    )}
                    <FormatIcon className={cn("w-4 h-4 flex-shrink-0", colors.textAccent)} />
                    <div className="min-w-0 flex-1">
                      <p className={cn("font-medium truncate", colors.textPrimary)}>
                        {kpi.metric_name || `KPI ${index + 1}`}
                      </p>
                      {!isExpanded && (
                        <div className="flex items-center gap-2">
                          {kpi.description && (
                            <p className={cn("text-xs truncate max-w-[200px]", colors.textMuted)} title={kpi.description}>
                              {kpi.description.length > 40 ? `${kpi.description.substring(0, 40)}...` : kpi.description}
                            </p>
                          )}
                          {kpi.target_value > 0 && (
                            <p className={cn("text-xs whitespace-nowrap", colors.textMuted)}>
                              {formatMetricValue(kpi.start_value || 0, kpi.metric_format)} → {formatMetricValue(kpi.target_value, kpi.metric_format)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isLocked && (
                      <span className="text-xs text-amber-600 dark:text-amber-400">🔒</span>
                    )}
                    {isEditing && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleKPIExpanded(kpi.kpi_id); }}
                          className={cn("p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); if (!isLocked) handleDeleteKPI(kpi.kpi_id); }}
                          disabled={isLocked}
                          className={cn(
                            "p-1 rounded text-red-500",
                            isLocked ? "opacity-30 cursor-not-allowed" : "hover:bg-red-100 dark:hover:bg-red-900/30"
                          )}
                          title={isLocked ? "Cannot delete KPI while goal is not in Draft status" : "Delete KPI"}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Expanded Edit Form */}
                {isExpanded && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                          Metric Name {isLocked && "🔒"}
                        </Label>
                        <Input
                          value={kpi.metric_name || ''}
                          onChange={(e) => handleKPIChange(kpi.kpi_id, 'metric_name', e.target.value)}
                          placeholder="e.g., Revenue Growth, Churn Rate"
                          disabled={isLocked || effectiveDisabled}
                          className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, isLocked && "opacity-60 cursor-not-allowed")}
                          data-testid={`kpi-metric-name-${kpi.kpi_id}`}
                        />
                      </div>
                      <div>
                        <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                          Format {isLocked && "🔒"}
                        </Label>
                        <Select 
                          value={kpi.metric_format || 'number'} 
                          onValueChange={(v) => handleKPIChange(kpi.kpi_id, 'metric_format', v)}
                          disabled={isLocked || effectiveDisabled}
                        >
                          <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isLocked && "opacity-60 cursor-not-allowed")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                            {METRIC_FORMAT_OPTIONS.map(f => {
                              const Icon = f.icon;
                              return (
                                <SelectItem key={f.value} value={f.value}>
                                  <div className="flex items-center gap-2">
                                    <Icon className="w-4 h-4" />
                                    <span>{f.label}</span>
                                    <span className="text-xs text-gray-400">({f.example})</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Description field for KPI */}
                    <div>
                      <Label className={colors.textSecondary}>Description (optional)</Label>
                      <Textarea
                        value={kpi.description || ''}
                        onChange={(e) => handleKPIChange(kpi.kpi_id, 'description', e.target.value)}
                        placeholder="Add context about this KPI, how it's measured, or why it matters..."
                        rows={2}
                        disabled={effectiveDisabled}
                        className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary)}
                        data-testid={`kpi-description-${kpi.kpi_id}`}
                      />
                    </div>
                    
                    {/* Direction and Interval */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                          Direction {isLocked && "🔒"}
                        </Label>
                        <Select 
                          value={kpi.direction || 'higher_is_better'} 
                          onValueChange={(v) => handleKPIChange(kpi.kpi_id, 'direction', v)}
                          disabled={isLocked || effectiveDisabled}
                        >
                          <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isLocked && "opacity-60 cursor-not-allowed")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                            {DIRECTION_OPTIONS.map(d => {
                              const Icon = d.icon;
                              return (
                                <SelectItem key={d.value} value={d.value}>
                                  <div className="flex items-center gap-2">
                                    <Icon className="w-4 h-4" />
                                    <span>{d.label}</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                          Measurement Interval {isLocked && "🔒"}
                        </Label>
                        <Select 
                          value={kpi.measurement_interval || 'monthly'} 
                          onValueChange={(v) => handleKPIChange(kpi.kpi_id, 'measurement_interval', v)}
                          disabled={isLocked || effectiveDisabled}
                        >
                          <SelectTrigger className={cn("mt-1", colors.bgInput, colors.borderPrimary, isLocked && "opacity-60 cursor-not-allowed")}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={cn(colors.bgCard, colors.borderPrimary)}>
                            {INTERVAL_OPTIONS.map(i => (
                              <SelectItem key={i.value} value={i.value}>
                                {i.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {/* Start Value and Target Value */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                          Start Value {isLocked && "🔒"}
                        </Label>
                        <Input
                          type="number"
                          value={kpi.start_value || 0}
                          onChange={(e) => handleKPIChange(kpi.kpi_id, 'start_value', parseFloat(e.target.value) || 0)}
                          disabled={isLocked || effectiveDisabled}
                          className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, isLocked && "opacity-60 cursor-not-allowed")}
                        />
                      </div>
                      <div>
                        <Label className={cn(colors.textSecondary, isLocked && "opacity-50")}>
                          Target Value {isLocked && "🔒"}
                        </Label>
                        <Input
                          type="number"
                          value={kpi.target_value || 0}
                          onChange={(e) => handleKPIChange(kpi.kpi_id, 'target_value', parseFloat(e.target.value) || 0)}
                          disabled={isLocked || effectiveDisabled}
                          className={cn("mt-1", colors.bgInput, colors.borderPrimary, colors.textPrimary, isLocked && "opacity-60 cursor-not-allowed")}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalKPIsPanel;
